import { AddressInfo, AddressOption } from '../components/common/CommonTypes';
import { ApiHelper } from './apiHelper';

/**
 * Client-side API wrapper for the address lookup endpoints.
 */
export class AddressManagerApi {
  /**
   * Loads available addresses for the supplied postcode/country.
   *
   * @param postcode Postcode to look up.
   * @param countryCode Two-letter ISO country code. Defaults to `GB` when not provided.
   * @param token Bearer token used for authenticated requests.
   * @param callback Invoked with the address options on success.
   * @param failureCallback Invoked with an error message on failure.
   * @param signal Abort signal used to cancel the request.
   */
  loadAddresses(
    postcode: string,
    countryCode: string,
    token: string,
    callback: (addresses: AddressOption[]) => void,
    failureCallback: (error: string) => void,
    signal: AbortSignal
  ): void {
    const addressCountryCode = countryCode || 'GB';
    const query = encodeURI(
      `?postcode=${postcode || ''}&countryCode=${addressCountryCode}`
    );
    const dataUrl = '/api/addressmanager/addresses' + query;
    new ApiHelper().getDataSecureAsync(
      dataUrl,
      token,
      callback,
      failureCallback,
      signal
    );
  }

  /**
   * Loads a single address record by moniker.
   *
   * @param moniker Address moniker/identifier returned by the address search.
   * @param token Bearer token used for authenticated requests.
   * @param callback Invoked with the resolved address on success.
   * @param failureCallback Invoked with an error message on failure.
   * @param signal Abort signal used to cancel the request.
   */
  loadAddress(
    moniker: string,
    token: string,
    callback: (foundAddress: AddressInfo) => void,
    failureCallback: (error: string) => void,
    signal: AbortSignal
  ): void {
    const query = encodeURI(`?moniker=${moniker || ''}`);
    const dataUrl = '/api/addressmanager/addressdetail' + query;
    new ApiHelper().getDataSecureAsync(
      dataUrl,
      token,
      callback,
      failureCallback,
      signal
    );
  }
}
